import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { NUMS_EN, NUMS_MS, clipFor, enClipFor, kategoriFor, quizStars, type Kategori, type KategoriItem } from "@jomcerdik/shared";
import { getLang, t, useLang } from "../../../lib/i18n";
import { api } from "../../../lib/api";
import { learnerKey } from "../../../lib/learnerId";
import { playClip, useAudioSource } from "../../../lib/bacaAudio";
import { speak } from "../../../lib/speak";
import { Card, Screen, StudyGate } from "../kit";
import { Burst, shuffle } from "../games/common";
import { Art, Mascot } from "../../../ui/Art";
import { BigButton, BigLink, Hero, SectionTitle } from "../../../ui/kit";
import { Icon, ListenDot, StarRow } from "../../../ui/Icon";

/* Tadika categories (remake): Menu → Belajar → Latihan → Keputusan. Pictures are drawn images; counting repeats one drawn object. */

const COLOURS: Record<string, string> = { merah: "#ef4444", biru: "#3b82f6", kuning: "#facc15", hijau: "#22c55e", oren: "#f97316", ungu: "#8b5cf6", "merah-jambu": "#f472b6", coklat: "#92400e", hitam: "#111827", putih: "#ffffff" };

/** Where each body part sits on the full-body mascot picture (percent of the picture box), so a tap or the highlight lands on it. */
export const BODY_SPOTS: Record<string, { x: number; y: number; w: number; h: number }> = {
  kepala: { x: 33, y: 3, w: 31, h: 28 }, mata: { x: 39, y: 20, w: 20, h: 9 }, telinga: { x: 32, y: 22, w: 8, h: 9 }, hidung: { x: 44, y: 24, w: 7, h: 6 },
  mulut: { x: 43, y: 28, w: 13, h: 6 }, bahu: { x: 30, y: 36, w: 13, h: 12 }, tangan: { x: 26, y: 59, w: 11, h: 12 }, kaki: { x: 32, y: 88, w: 13, h: 10 },
  lutut: { x: 37, y: 70, w: 10, h: 8 }, jari: { x: 63, y: 59, w: 10, h: 12 },
};

/** A word the pronunciation lexicon does not know yet must never break the page: it is read by the device's Malay voice instead. */
const safeClip = (kind: "word" | "line", text: string) => { try { return clipFor(kind, text); } catch { return null; } };
/** Tadika content is Malay for BM and English for every other app language (no mixed languages). */
const contentLang = () => (getLang() === "ms" ? "ms" : "en");
const say = (text: string, kind: "word" | "line" = "word") => {
  if (contentLang() === "en") { playClip(enClipFor(kind, text), text, 0.7); return; }
  const c = safeClip(kind, text); if (c) playClip(c, text, 0.7); else speak(text, 0.7);
};
const artOf = (cat: Kategori, item: KategoriItem) => item.art ?? `${cat.id}/${item.id}`;

function Shape({ id, className = "h-20 w-20" }: { id: string; className?: string }) {
  const f = "#60a5fa", s = "#1d4ed8";
  return (
    <svg viewBox="0 0 100 100" className={className} role="img" aria-label={id}>
      {id === "bulat" && <circle cx="50" cy="50" r="40" fill={f} stroke={s} strokeWidth="4" />}
      {id === "segi-tiga" && <polygon points="50,10 92,88 8,88" fill={f} stroke={s} strokeWidth="4" strokeLinejoin="round" />}
      {id === "segi-empat-sama" && <rect x="14" y="14" width="72" height="72" rx="4" fill={f} stroke={s} strokeWidth="4" />}
      {id === "segi-empat-tepat" && <rect x="6" y="28" width="88" height="44" rx="4" fill={f} stroke={s} strokeWidth="4" />}
      {id === "bintang" && <polygon points="50,8 61,38 93,38 67,57 77,89 50,70 23,89 33,57 7,38 39,38" fill={f} stroke={s} strokeWidth="4" strokeLinejoin="round" />}
      {id === "hati" && <path d="M50 88 C10 56 6 30 26 18 C40 10 50 22 50 30 C50 22 60 10 74 18 C94 30 90 56 50 88Z" fill={f} stroke={s} strokeWidth="4" strokeLinejoin="round" />}
      {id === "bujur" && <ellipse cx="50" cy="50" rx="30" ry="42" fill={f} stroke={s} strokeWidth="4" />}
    </svg>
  );
}

/** The picture (or shape / colour / letter) that stands for an item in this category. */
function Face({ cat, item, size = "h-24 w-24" }: { cat: Kategori; item: KategoriItem; size?: string }) {
  if (cat.id === "bentuk") return <Shape id={item.id} className={size} />;
  if (cat.id === "warna") return <span className={`${size} rounded-full ring-4 ring-white shadow-inner`} style={{ background: COLOURS[item.id] }} aria-label={item.ms} role="img" />;
  if (cat.id === "huruf") return <span className={`flex ${size} items-center justify-center text-5xl font-black text-rose-500`}>{item.id.slice(-1).toUpperCase()}{item.id.slice(-1)}</span>;
  return <Art id={artOf(cat, item)} alt={item.ms} label={item.ms} className={size} />;
}

type Q = { item: KategoriItem; choices: KategoriItem[]; count?: number };

function makeQuiz(cat: Kategori): Q[] {
  const pool = cat.items;
  if (cat.id === "nombor") {
    return shuffle(Array.from({ length: 10 }, (_, i) => i + 1)).slice(0, 8).map((n) => {
      const obj = cat.items[Math.floor(Math.random() * cat.items.length)]!;
      const nums = shuffle([n, ...shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9, 10].filter((x) => x !== n)).slice(0, 3)]);
      return { item: obj, count: n, choices: nums.map((x) => ({ id: String(x), ms: String(x), draw: "" })) };
    });
  }
  return shuffle(pool).slice(0, Math.min(8, pool.length)).map((item) => ({ item, choices: shuffle([item, ...shuffle(pool.filter((p) => p.id !== item.id)).slice(0, 3)]) }));
}

const nums = () => (contentLang() === "en" ? NUMS_EN : NUMS_MS);

function questionText(cat: Kategori, q: Q): string {
  if (cat.id === "nombor") return t("Berapa banyak?");
  if (cat.id === "huruf") return t("Apakah huruf pertama?");
  if (cat.id === "warna") return `${t("Pilih warna")} ${q.item.ms}`;
  if (cat.id === "bentuk") return `${t("Pilih bentuk")} ${q.item.ms}`;
  return `${t("Sentuh")} ${q.item.ms}`;
}
const questionSay = (cat: Kategori, q: Q) => {
  if (contentLang() === "en") return cat.id === "nombor" ? "How many?" : cat.id === "huruf" ? q.item.ms : cat.id === "warna" ? `Pick the colour ${q.item.ms}` : cat.id === "bentuk" ? `Pick the shape ${q.item.ms}` : `Touch ${q.item.ms}`;
  return cat.id === "nombor" ? "Berapa banyak?" : cat.id === "huruf" ? q.item.ms : cat.id === "warna" ? `Pilih warna ${q.item.ms}` : cat.id === "bentuk" ? `Pilih bentuk ${q.item.ms}` : `Sentuh ${q.item.ms}`;
};

/** Best score stays on this device (per child); every finished round is also sent to the server so the parent sees it. */
function saveKategori(id: string, first: number, total: number) {
  try { const k = learnerKey(`tadika.kat.${id}`); const best = Number(localStorage.getItem(k) ?? 0); if (first > best) localStorage.setItem(k, String(first)); } catch { /* optional */ }
  if (total > 0) void api("/api/learn/games/kategori", { method: "POST", json: { score: Math.min(first, total), max: total, details: { id } } }).catch(() => undefined);
}

export function KategoriPage() {
  return <Screen><StudyGate><KategoriScreen /></StudyGate></Screen>;
}

type Mode = "menu" | "learn" | "quiz" | "result";

function KategoriScreen() {
  const id = useParams().id;
  const appLang = useLang();
  const cat = useMemo(() => kategoriFor(appLang === "ms" ? "ms" : "en").find((c) => c.id === id), [appLang, id]);
  const [mode, setMode] = useState<Mode>("menu");
  const [focus, setFocus] = useState<KategoriItem | null>(null);
  const [score, setScore] = useState({ first: 0, total: 0 });
  const clips = useMemo(() => (cat ? cat.items.flatMap((i) => { const c = appLang === "ms" ? safeClip("word", i.ms) : enClipFor("word", i.ms); return c ? [c] : []; }) : []), [cat, appLang]);
  useAudioSource(clips);
  useEffect(() => { setMode("menu"); setFocus(null); window.scrollTo(0, 0); }, [id]);
  if (!cat) return <Card>{t("Kategori tidak dijumpai.")} <Link className="underline" to="/belajar/tadika">{t("Kembali")}</Link></Card>;
  const pick = (i: KategoriItem) => { setFocus(i); say(i.ayat ?? i.ms, i.ayat ? "line" : "word"); };

  if (mode === "quiz" && cat.id === "badan") return <BadanQuiz cat={cat} onDone={(first, total) => { setScore({ first, total }); setMode("result"); saveKategori(cat.id, first, total); }} onExit={() => setMode("menu")} />;
  if (mode === "quiz") return <Quiz cat={cat} onDone={(first, total) => { setScore({ first, total }); setMode("result"); saveKategori(cat.id, first, total); }} onExit={() => setMode("menu")} />;
  if (mode === "result") return <Result cat={cat} first={score.first} total={score.total} onAgain={() => setMode("quiz")} />;

  if (cat.id === "badan") return <BadanLearn cat={cat} focus={focus} onPick={pick} onStart={() => setMode("quiz")} />;
  if (cat.id === "nombor") return <NomborLearn cat={cat} onStart={() => setMode("quiz")} />;

  return (
    <>
      <Link to="/belajar/tadika" className="mb-3 inline-flex min-h-10 items-center rounded-full bg-card px-4 text-sm font-bold text-slate-700 ring-1 ring-black/10">‹ {t("Semua kategori")}</Link>
      <Hero title={cat.ms} text={t(cat.blurb)} pose={cat.mascot.replace("mascot/", "")}>
        <BigButton tone="green" onClick={() => setMode("quiz")}>{t("Mula latihan")}</BigButton>
      </Hero>
      <SectionTitle>{t("Tekan gambar untuk dengar")}</SectionTitle>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cat.items.map((i, n) => (
          <button key={i.id} type="button" onClick={() => pick(i)} aria-label={i.ms}
            className={`pastel-${["blue", "pink", "green", "yellow", "purple", "orange"][n % 6]} flex flex-col items-center gap-2 rounded-3xl p-4 shadow-sm transition active:scale-95 ${focus?.id === i.id ? "ring-4 ring-amber-400" : ""}`}>
            <Face cat={cat} item={i} size="h-24 w-24 sm:h-28 sm:w-28" />
            <span className="text-lg font-black text-slate-900">{i.ms}</span>
            <Icon name="speaker" className="h-6 w-6 text-sky-600" />
          </button>
        ))}
      </div>
      {focus && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-black/10 bg-card/95 p-3 backdrop-blur" role="status">
          <div className="mx-auto flex max-w-xl items-center gap-4">
            <Face cat={cat} item={focus} size="h-20 w-20" />
            <p className="flex-1 text-3xl font-black text-slate-900">{focus.ayat ?? focus.ms}</p>
            <button type="button" aria-label={t("Dengar lagi")} onClick={() => pick(focus)} className="grid h-14 w-14 place-items-center rounded-full bg-sky-500 text-white"><Icon name="speaker" className="h-7 w-7" /></button>
          </div>
        </div>
      )}
      <div className="h-24" />
    </>
  );
}

/** Nombor dan Pengiraan, learning screen: numbers 1–10, each card shows that many copies of one drawn object; tap to hear the number. */
function NomborLearn({ cat, onStart }: { cat: Kategori; onStart: () => void }) {
  const [focus, setFocus] = useState<number | null>(null);
  const objFor = (n: number) => cat.items[(n - 1) % cat.items.length]!;
  const pick = (n: number) => { setFocus(n); say(nums()[n]!); };
  return (
    <>
      <Link to="/belajar/tadika" className="mb-3 inline-flex min-h-10 items-center rounded-full bg-card px-4 text-sm font-bold text-slate-700 ring-1 ring-black/10">‹ {t("Semua kategori")}</Link>
      <Hero title={cat.ms} text={t(cat.blurb)} pose={cat.mascot.replace("mascot/", "")}>
        <BigButton tone="green" onClick={onStart}>{t("Mula latihan")}</BigButton>
      </Hero>
      <SectionTitle>{t("Kira gambar dan tekan untuk dengar")}</SectionTitle>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
          const obj = objFor(n);
          return (
            <button key={n} type="button" onClick={() => pick(n)} aria-label={`${n} ${nums()[n]}`}
              className={`pastel-${["blue", "pink", "green", "yellow", "purple", "orange"][(n - 1) % 6]} flex items-center gap-4 rounded-3xl p-4 text-left shadow-sm transition active:scale-95 ${focus === n ? "ring-4 ring-amber-400" : ""}`}>
              <span className="flex w-20 shrink-0 flex-col items-center">
                <span className="text-6xl font-black leading-none text-sky-600">{n}</span>
                <span className="mt-1 text-base font-black text-slate-900">{nums()[n]}</span>
              </span>
              <span className="flex flex-1 flex-wrap items-center gap-1" aria-hidden="true">
                {Array.from({ length: n }, (_, k) => <Art key={k} id={artOf(cat, obj)} alt="" className="h-10 w-10 sm:h-12 sm:w-12" />)}
              </span>
              <Icon name="speaker" className="h-6 w-6 shrink-0 text-sky-600" />
            </button>
          );
        })}
      </div>
      <div className="h-8" />
    </>
  );
}

/** Classroom backdrop with the full-body mascot; `mark` is the part to highlight, `bubble` the speech bubble on it. */
function BadanStage({ cat, mark, ok, bubble, onTap, tapping, shake }: { cat: Kategori; mark: KategoriItem | null; ok?: boolean; bubble?: string; onTap?: (i: KategoriItem) => void; tapping?: boolean; shake?: boolean }) {
  const sp = mark ? BODY_SPOTS[mark.id] : undefined;
  return (
    <div className={`relative mx-auto aspect-square w-full max-w-[520px] overflow-hidden rounded-[32px] bg-sky-200 shadow-md sm:aspect-auto sm:h-[470px] sm:max-w-none ${shake ? "animate-shake" : ""}`}>
      <Art id="cover/kelas" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-white/10" />
      <div className="relative mx-auto aspect-square h-full">
        <Art id="mascot/boy-full" alt={t("Adi, murid tadika")} className="absolute inset-0 h-full w-full drop-shadow-xl" />
        {cat.items.map((i) => { const b = BODY_SPOTS[i.id]; if (!b) return null; const on = mark?.id === i.id; return (
          <button key={i.id} type="button" aria-label={i.ms} onClick={() => onTap?.(i)} tabIndex={onTap ? 0 : -1}
            className={`absolute rounded-full border-4 ${on ? `spot-on ${ok ? "border-emerald-500 bg-emerald-300/50" : "border-amber-400 bg-amber-300/50"}` : tapping ? "border-dashed border-white/90 bg-white/20" : "border-transparent"}`}
            style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%` }} />); })}
        {sp && bubble && (
          <span className="absolute -translate-y-full whitespace-nowrap rounded-2xl bg-white px-3 py-1 text-lg font-black text-slate-900 shadow-lg ring-2 ring-amber-300" style={{ left: `${Math.min(sp.x + sp.w, 66)}%`, top: `${Math.max(sp.y - 1, 12)}%` }}>{bubble}</span>
        )}
      </div>
    </div>
  );
}

const CHIPS = ["pastel-blue", "pastel-pink", "pastel-green", "pastel-yellow", "pastel-purple", "pastel-orange"];

/** A picture card with its label (the choices in the PDF). */
function PartCard({ item, state, onClick, n }: { item: KategoriItem; state?: "on" | "ok" | "bad" | "dim"; onClick: () => void; n: number }) {
  const ring = state === "on" ? "ring-4 ring-amber-400 bg-amber-50" : state === "ok" ? "ring-4 ring-emerald-500 bg-emerald-50" : state === "bad" ? "animate-shake opacity-50 ring-4 ring-red-400" : state === "dim" ? "opacity-50 ring-2 ring-black/5" : "ring-2 ring-black/5";
  return (
    <button type="button" onClick={onClick} aria-label={item.ms} disabled={state === "bad"} className={`${state ? "" : CHIPS[n % 6]} flex flex-col items-center gap-1 rounded-3xl bg-white p-3 shadow-sm transition active:scale-95 ${ring}`}>
      <Art id={`badan/${item.id}`} alt="" label={item.ms} className="h-24 w-24 sm:h-28 sm:w-28" />
      <span className="rounded-full bg-sky-100 px-4 py-0.5 text-base font-black text-slate-900">{item.ms}</span>
    </button>
  );
}

/** The module header chip of the PDF: small mascot, "Modul Pembelajaran" and the category name. */
function ModuleChip({ cat }: { cat: Kategori }) {
  return (
    <div className="flex items-center gap-3 rounded-3xl bg-white p-3 shadow-sm ring-1 ring-sky-100">
      <Mascot pose="girl-wave" className="h-16 w-16 shrink-0" />
      <div><p className="text-xs font-bold text-sky-600">{t("Modul Pembelajaran")}</p><p className="text-xl font-black text-slate-900">{cat.ms}</p><p className="text-xs text-slate-600">{t(cat.blurb)}</p></div>
    </div>
  );
}

/** Anggota Badan, learning screen: tap a part picture (or the body), see it marked, hear it, then "Sentuh <bahagian>". */
function BadanLearn({ cat, focus, onPick, onStart }: { cat: Kategori; focus: KategoriItem | null; onPick: (i: KategoriItem) => void; onStart: () => void }) {
  const [phase, setPhase] = useState<"look" | "ask" | "ok">("look");
  const [miss, setMiss] = useState(false);
  const [stars, setStars] = useState(0);
  useEffect(() => { setPhase("look"); setMiss(false); }, [focus?.id]);
  const idx = focus ? cat.items.findIndex((x) => x.id === focus.id) : -1;
  const next = cat.items[(idx + 1) % cat.items.length]!;
  const tap = (i: KategoriItem) => {
    if (phase !== "ask" || !focus) { onPick(i); return; }
    if (i.id === focus.id) { setPhase("ok"); setMiss(false); setStars((n) => n + 1); say(focus.ms); } else { setMiss(true); window.setTimeout(() => setMiss(false), 700); }
  };
  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Link to="/belajar/tadika" className="inline-flex min-h-10 items-center rounded-full bg-white px-4 text-sm font-bold text-slate-700 ring-1 ring-black/10">‹ {t("Semua kategori")}</Link>
        <span className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-1.5 text-lg font-black text-slate-900 ring-1 ring-black/10"><Icon name="star" className="h-6 w-6 text-amber-400" />{stars}</span>
      </div>
      <ModuleChip cat={cat} />
      <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="space-y-3">
          <BadanStage cat={cat} mark={focus} ok={phase === "ok"} bubble={focus?.ms} onTap={tap} tapping={phase === "ask"} shake={miss} />
          <div className="pastel-blue flex flex-col items-center gap-3 rounded-[28px] p-4 text-center sm:flex-row sm:text-left">
            {!focus ? (
              <p className="flex-1 text-xl font-black text-slate-900">{t("Pilih satu bahagian badan untuk bermula.")}</p>
            ) : (
              <>
                <Art id={`badan/${focus.id}`} alt={focus.ms} label={focus.ms} className="h-28 w-28 shrink-0 rounded-3xl bg-white p-1" />
                <div className="flex-1">
                  <p className="text-4xl font-black text-slate-900">{focus.ms}</p>
                  {phase === "ask" && <p className="text-lg font-black text-slate-800" role="status">{miss ? t("Cuba lagi") : `${t("Sentuh")} ${focus.ms} ${t("pada badan Adi.")}`}</p>}
                  {phase === "ok" && <p className="text-xl font-black text-emerald-700" role="status">{t("Betul! Bagus!")}</p>}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <ListenDot onClick={() => say(focus.ms)} label={t("Dengar lagi")} className="h-14 w-14" />
                  {phase === "look" && <BigButton tone="green" onClick={() => { setPhase("ask"); say(contentLang() === "en" ? `Touch ${focus.ms}` : `Sentuh ${focus.ms}`, "line"); }}>{t("Sentuh")} {focus.ms}</BigButton>}
                  {phase === "ok" && <BigButton tone="green" onClick={() => onPick(next)}>{t("Seterusnya")} ›</BigButton>}
                </div>
              </>
            )}
          </div>
        </div>
        <div>
          <SectionTitle>{t("Pilih bahagian badan")}</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            {cat.items.map((i, n) => <PartCard key={i.id} item={i} n={n} state={focus?.id === i.id ? "on" : undefined} onClick={() => tap(i)} />)}
          </div>
        </div>
      </div>
      <div className="mt-5 flex justify-center"><BigButton tone="green" onClick={onStart}>{t("Mula latihan")} ›</BigButton></div>
    </>
  );
}

/** Anggota Badan, practice (PDF T05): the part is marked on the mascot; pick its picture from four cards. */
function BadanQuiz({ cat, onDone, onExit }: { cat: Kategori; onDone: (first: number, total: number) => void; onExit: () => void }) {
  const qs = useMemo(() => shuffle(cat.items).slice(0, 10).map((item) => ({ item, choices: shuffle([item, ...shuffle(cat.items.filter((p) => p.id !== item.id)).slice(0, 3)]) })), [cat]);
  const [n, setN] = useState(0);
  const [wrong, setWrong] = useState<string[]>([]);
  const [done, setDone] = useState(false);
  const [first, setFirst] = useState(0);
  const q = qs[n]!;
  const spoken = useRef(-1);
  useEffect(() => { if (spoken.current !== n) { spoken.current = n; say(q.item.ms); } }, [n]);
  const choose = (c: KategoriItem) => {
    if (done || wrong.includes(c.id)) return;
    if (c.id === q.item.id) { setDone(true); if (wrong.length === 0) setFirst((f) => f + 1); say(q.item.ms); } else setWrong((w) => [...w, c.id]);
  };
  const next = () => { if (n + 1 >= qs.length) onDone(first, qs.length); else { setN(n + 1); setWrong([]); setDone(false); } };
  const running = quizStars(first, Math.max(n + (done ? 1 : 0), 1));
  return (
    <>
      <div className="mb-3 flex items-center gap-3 rounded-3xl bg-white p-2 shadow-sm ring-1 ring-sky-100">
        <button type="button" onClick={onExit} aria-label={t("Tutup")} className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-sky-500 text-white"><Icon name="home" /></button>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-slate-600">{t("Soalan")} {n + 1} / {qs.length}</p>
          <div className="h-3 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${((n + (done ? 1 : 0)) / qs.length) * 100}%` }} /></div>
        </div>
        <StarRow n={done || n > 0 ? running : 0} />
      </div>
      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)_340px]">
        <div className="order-2 space-y-3 lg:order-1">
          <ModuleChip cat={cat} />
          <div className="rounded-[28px] bg-white p-4 shadow-sm ring-1 ring-sky-100">
            <span className="rounded-full bg-sky-500 px-3 py-1 text-sm font-extrabold text-white">{t("Soalan")} {n + 1} / {qs.length}</span>
            <p className="mt-2 text-3xl font-black leading-tight text-sky-900">{t("Apakah nama bahagian ini?")}</p>
            <div className="mt-3 flex items-center gap-3 rounded-2xl bg-sky-50 p-3">
              <ListenDot onClick={() => say(q.item.ms)} label={t("Dengar lagi")} className="h-14 w-14" />
              <p className="text-sm font-bold text-slate-700">{t("Tekan untuk dengar sebutan")}</p>
            </div>
            <button type="button" onClick={() => say(q.item.ms)} className="mt-3 flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-sky-500 px-4 text-base font-extrabold text-white"><Icon name="refresh" className="h-5 w-5" />{t("Ulang Audio")}</button>
          </div>
        </div>
        <div className="order-1 lg:order-2"><BadanStage cat={cat} mark={q.item} ok={done} bubble={done ? q.item.ms : "?"} /></div>
        <div className="order-3 grid grid-cols-2 content-start gap-3">
          {q.choices.map((c, i) => <PartCard key={c.id} item={c} n={i} onClick={() => choose(c)} state={wrong.includes(c.id) ? "bad" : done && c.id === q.item.id ? "ok" : done ? "dim" : undefined} />)}
        </div>
      </div>
      <p className="mt-3 min-h-8 text-center text-xl font-black text-slate-900" role="status">{done ? t("Betul! Bagus!") : wrong.length ? t("Cuba lagi") : ""}</p>
      {done && <div className="flex justify-center lg:justify-end"><button type="button" onClick={next} className="flex min-h-14 items-center gap-2 rounded-full bg-gradient-to-b from-green-400 to-emerald-600 px-10 text-xl font-black text-white shadow-lg ring-4 ring-white active:scale-95">{n + 1 >= qs.length ? t("Lihat keputusan") : t("Seterusnya")} <Icon name="arrow" /></button></div>}
    </>
  );
}

function Quiz({ cat, onDone, onExit }: { cat: Kategori; onDone: (first: number, total: number) => void; onExit: () => void }) {
  const qs = useMemo(() => makeQuiz(cat), [cat]);
  const [n, setN] = useState(0);
  const [wrong, setWrong] = useState<string[]>([]);
  const [done, setDone] = useState(false);
  const [first, setFirst] = useState(0);
  const [burst, setBurst] = useState(0);
  const [msg, setMsg] = useState("");
  const q = qs[n]!;
  const spoken = useRef(-1);
  useEffect(() => { if (spoken.current !== n) { spoken.current = n; setMsg(""); say(questionSay(cat, q), "line"); } }, [n]);

  function choose(c: KategoriItem) {
    if (done || wrong.includes(c.id)) return;
    const ok = c.id === (cat.id === "nombor" ? String(q.count) : q.item.id);
    if (ok) {
      setDone(true); setBurst((b) => b + 1); setMsg(t("Betul! Bagus!"));
      if (wrong.length === 0) setFirst((f) => f + 1);
      say(cat.id === "nombor" ? nums()[q.count!]! : q.item.ms);
    } else { setWrong((w) => [...w, c.id]); setMsg(t("Cuba lagi")); }
  }
  const next = () => { if (n + 1 >= qs.length) onDone(first, qs.length); else { setN(n + 1); setWrong([]); setDone(false); } };

  return (
    <>
      <Burst show={burst} emoji={["★", "✦"]} />
      <div className="mb-3 flex items-center gap-3">
        <button type="button" onClick={onExit} aria-label={t("Tutup")} className="grid h-11 w-11 place-items-center rounded-full bg-card text-lg ring-1 ring-black/10">✕</button>
        <div className="h-3 flex-1 overflow-hidden rounded-full bg-black/10"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${(n / qs.length) * 100}%` }} /></div>
        <span className="text-sm font-bold text-slate-600">{n + 1}/{qs.length}</span>
      </div>
      <div className="pastel-blue mb-4 flex items-center gap-3 rounded-[28px] p-4">
        <Mascot pose={done ? "boy-cheer" : "boy-think"} className="h-24 w-24 shrink-0" />
        <div className="flex-1">
          <p className="text-2xl font-black text-slate-900 sm:text-3xl">{questionText(cat, q)}</p>
          <button type="button" onClick={() => say(questionSay(cat, q), "line")} className="mt-1 rounded-full bg-white/80 px-4 py-1.5 text-sm font-bold text-brand dark:bg-sky-900/60 dark:text-sky-100"><Icon name="speaker" className="mr-1 inline h-4 w-4" />{t("Dengar soalan")}</button>
        </div>
      </div>
      {cat.id === "nombor" && (
        <div className="pastel-yellow mb-4 flex flex-wrap items-center justify-center gap-2 rounded-3xl p-4" aria-label={t("Gambar untuk dikira")}>
          {Array.from({ length: q.count! }, (_, i) => <Art key={i} id={`nombor/${q.item.id}`} alt="" className="h-16 w-16 sm:h-20 sm:w-20" />)}
        </div>
      )}
      {(cat.id === "huruf" || cat.id === "warna") && <div className="mb-4 flex justify-center"><Art id={artOf(cat, q.item)} alt={q.item.ms} label={q.item.ms} className="h-36 w-36" /></div>}
      {(
        <div className="grid grid-cols-2 gap-3">
          {q.choices.map((c) => {
            const bad = wrong.includes(c.id);
            return (
              <button key={c.id} type="button" onClick={() => choose(c)} disabled={bad} aria-label={c.ms}
                className={`flex min-h-32 flex-col items-center justify-center gap-1 rounded-3xl bg-card p-3 shadow-sm ring-2 transition active:scale-95 ${bad ? "animate-shake opacity-40 ring-red-300" : done && c.id === (cat.id === "nombor" ? String(q.count) : q.item.id) ? "ring-emerald-500" : "ring-black/5"}`}>
                {cat.id === "nombor" ? <span className="text-6xl font-black text-sky-600">{c.ms}</span>
                  : cat.id === "huruf" ? <span className="text-5xl font-black text-rose-500">{c.id.slice(-1).toUpperCase()}{c.id.slice(-1)}</span>
                  : <Face cat={cat} item={c} size="h-24 w-24" />}
              </button>
            );
          })}
        </div>
      )}
      <p className="mt-4 min-h-8 text-center text-xl font-black text-slate-900" role="status">{msg}</p>
      {done && <div className="mt-2 flex justify-center"><BigButton tone="green" onClick={next}>{n + 1 >= qs.length ? t("Lihat keputusan") : t("Seterusnya")} ›</BigButton></div>}
    </>
  );
}

function Result({ cat, first, total, onAgain }: { cat: Kategori; first: number; total: number; onAgain: () => void }) {
  const stars = quizStars(first, total);
  return (
    <div className="pastel-green mx-auto max-w-lg rounded-[32px] p-6 text-center">
      <Mascot pose={stars >= 2 ? "boy-cheer" : "girl-cheer"} className="mx-auto h-44 w-44" />
      <h1 className="mt-2 text-3xl font-black text-slate-900">{t("Tahniah!")}</h1>
      <p className="mt-1 text-lg text-slate-700">{cat.ms}: {first}/{total} {t("betul pada cubaan pertama")}</p>
      <p className="my-3 flex justify-center"><StarRow n={stars} className="h-12 w-12" /></p>
      <div className="flex flex-wrap justify-center gap-3">
        <BigButton tone="green" onClick={onAgain}>{t("Main lagi")}</BigButton>
        <BigLink tone="soft" to="/belajar/tadika">{t("Semua kategori")}</BigLink>
      </div>
    </div>
  );
}
